import { useEffect, useState } from "react";
import styles from "./LoadTestAccounts.module.css";

export type RevealedLoginCode = {
  code: string;
  expiresAt: string;
  remainingAtReveal: number;
  revealedAt: number;
};

export function revealedLoginCode(
  code: string,
  expiresAt: string,
  expiresInSeconds?: number,
): RevealedLoginCode {
  return {
    code,
    expiresAt,
    remainingAtReveal:
      expiresInSeconds === undefined
        ? new Date(expiresAt).getTime() - Date.now()
        : expiresInSeconds * 1000,
    revealedAt: performance.now(),
  };
}

function remainingOf({ remainingAtReveal, revealedAt }: RevealedLoginCode): number {
  return remainingAtReveal - (performance.now() - revealedAt);
}

function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function LoginCode({ revealed }: { revealed: RevealedLoginCode }) {
  const [remaining, setRemaining] = useState(() => remainingOf(revealed));
  const expired = remaining <= 0;

  useEffect(() => {
    const initial = remainingOf(revealed);
    setRemaining(initial);
    if (initial <= 0) return;
    const interval = setInterval(() => {
      const next = remainingOf(revealed);
      setRemaining(next);
      if (next <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [revealed]);

  const expiryTime = new Date(revealed.expiresAt).toLocaleTimeString();

  if (expired) {
    return (
      <span className={styles.muted}>
        The code expired at {expiryTime}. Reveal it again for a new one.
      </span>
    );
  }

  return (
    <span className={styles.code}>
      <span>
        Code <code className={styles.codeValue}>{revealed.code}</code>, expires at {expiryTime}
      </span>{" "}
      <span className={styles.muted} aria-hidden="true">
        ({formatRemaining(remaining)} left)
      </span>
    </span>
  );
}
